import React, { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { RouteNotFound } from './common/components/route/RouteNotFound';
import { IS_IOS } from './common/constants';
import { Account } from './features/account/Account';
import { AccountDashboard } from './features/account/AccountDashboard';
import { AddDesignatedCircuit } from './features/account/energy-modes/AddDesignatedCircuit';
import { DesignatedCircuits } from './features/account/energy-modes/DesignatedCircuits';
import { EnergyModesHome } from './features/account/energy-modes/EnergyModesHome';
import { EnergyModesOnboardingFinish } from './features/account/energy-modes/EnergyModesOnboardingFinish';
import { EnergyModesOnboardingStart } from './features/account/energy-modes/EnergyModesOnboardingStart';
import { HardwareConfiguration } from './features/account/hardware/HardwareConfiguration';
import { HardwareConnection } from './features/account/hardware/HardwareConnection';
import { HardwareDetails } from './features/account/hardware/HardwareDetails';
import { HardwareList } from './features/account/hardware/HardwareList';
import { InverterPendingInternetConnection } from './features/account/hardware/inverter/InverterPendingInternetConnection';
import { InverterPowerUpInstructions } from './features/account/hardware/inverter/InverterPowerUpInstructions';
import { InverterScanBarcode } from './features/account/hardware/inverter/InverterScanBarcode';
import { InverterSetupComplete } from './features/account/hardware/inverter/InverterSetupComplete';
import { InverterStart } from './features/account/hardware/inverter/InverterStart';
import { InverterWifiCredentials } from './features/account/hardware/inverter/InverterWifiCredentials';
import { MeterConnectInstructions } from './features/account/hardware/meter/MeterConnectInstructions';
import { MeterConnectionTests } from './features/account/hardware/meter/MeterConnectionTests';
import { MeterManualSerialEntry } from './features/account/hardware/meter/MeterManualSerialEntry';
import { MeterPowerUpInstructions } from './features/account/hardware/meter/MeterPowerUpInstructions';
import { MeterRegistration } from './features/account/hardware/meter/MeterRegistration';
import { MeterScanBarcode } from './features/account/hardware/meter/MeterScanBarcode';
import { MeterSetupComplete } from './features/account/hardware/meter/MeterSetupComplete';
import { MeterStart } from './features/account/hardware/meter/MeterStart';
import { TeslaConnect } from './features/account/hardware/tesla/TeslaConnect';
import { TeslaOAuthRedirect } from './features/account/hardware/tesla/TeslaOAuthRedirect';
import { PersonalDetails } from './features/account/PersonalDetails';
import { EditFamilyMembers } from './features/account/settings/family-members/EditFamilyMembers';
import { ViewFamilyMembers } from './features/account/settings/family-members/ViewFamilyMembers';
import { LicensesDetail } from './features/account/settings/licenses/LicensesDetail';
import { LicensesPackagesList } from './features/account/settings/licenses/LicensesPackagesList';
import { LicensesPublishersList } from './features/account/settings/licenses/LicensesPublishersList';
import { DeleteAccountArchive } from './features/account/settings/privacy/delete-account/DeleteAccountArchive';
import { DeleteAccountConfirm } from './features/account/settings/privacy/delete-account/DeleteAccountConfirm';
import { DeleteAccountStart } from './features/account/settings/privacy/delete-account/DeleteAccountStart';
import { DeleteAccountVerify } from './features/account/settings/privacy/delete-account/DeleteAccountVerify';
import { PrivacySettingsHome } from './features/account/settings/privacy/PrivacySettingsHome';
import { SettingsHome } from './features/account/settings/SettingsHome';
import { SettingsLanguage } from './features/account/settings/SettingsLanguage';
import { WidgetSettings } from './features/account/settings/WidgetSettings';
import { AddWifiDetails } from './features/account/settings/wifi-settings/AddWifiDetails';
import { EditWifiDetails } from './features/account/settings/wifi-settings/EditWifiDetails';
import { ViewWifiSettings } from './features/account/settings/wifi-settings/ViewWifiSettings';
import { Support } from './features/account/Support';
import { ActiveProgram } from './features/account/vpp/active-program/ActiveProgram';
import { EnrolmentForm } from './features/account/vpp/EnrolmentForm';
import { JoinProgram } from './features/account/vpp/JoinProgram';
import { VPPHome } from './features/account/vpp/VPPHome';
import { VPPRoutes } from './features/account/vpp/VPPRoutes';
import { Activity } from './features/activity/Activity';
import { Login } from './features/auth/Login';
import { Logout } from './features/auth/logout/Logout';
import { UpdateRequired } from './features/auth/UpdateRequired';
import { BatteryHome } from './features/battery/BatteryHome';
import { SetReserveCapacity } from './features/battery/SetReserveCapacity';
import { AddDeviceHome } from './features/devices/AddDeviceHome';
import { AylaDeviceDetails } from './features/devices/ayla-switches/AylaDeviceDetails';
import { EditAylaDevice } from './features/devices/ayla-switches/EditAylaDevice';
import { AndroidCommissioningWizard } from './features/devices/ayla-switches/matter/commissioning/AndroidCommissioningWizard';
import { IOSCommissioningWizard } from './features/devices/ayla-switches/matter/commissioning/IOSCommissioningWizard';
import { ViewAylaDevice } from './features/devices/ayla-switches/ViewAylaDevice';
import { AylaDeviceRegistration } from './features/devices/ayla-switches/wifi/commissioning/AylaDeviceRegistration';
import { WifiDeviceLocalSetup } from './features/devices/ayla-switches/wifi/commissioning/WifiDeviceLocalSetup';
import { WifiDeviceStart } from './features/devices/ayla-switches/wifi/commissioning/WifiDeviceStart';
import { DevicesHome } from './features/devices/DevicesHome';
import { EditDevice } from './features/devices/EditDevice';
import { EditSwitch } from './features/devices/switches/EditSwitch';
import { ViewSwitch } from './features/devices/switches/ViewSwitch';
import { ViewDevice } from './features/devices/ViewDevice';
import { Eco } from './features/eco/Eco';
import { ChargingActivity } from './features/ev-charger/activity/ChargingActivity';
import { DeviceDetails } from './features/ev-charger/dashboard/DeviceDetails';
import { DeviceStatus } from './features/ev-charger/dashboard/DeviceStatus';
import { EVChargerHome } from './features/ev-charger/dashboard/EVChargerHome';
import { NoDevice } from './features/ev-charger/dashboard/NoDevice';
import { DeviceJoin } from './features/ev-charger/device-setup/DeviceJoin';
import { DevicePowerUpInstructions } from './features/ev-charger/device-setup/DevicePowerUpInstructions';
import { DeviceRegister } from './features/ev-charger/device-setup/DeviceRegister';
import { DeviceScan } from './features/ev-charger/device-setup/DeviceScan';
import { SetupFail } from './features/ev-charger/device-setup/SetupFail';
import { SetupSuccess } from './features/ev-charger/device-setup/SetupSuccess';
import { WifiSetupFail } from './features/ev-charger/device-setup/WifiSetupFail';
import { DeviceChargingSchedule } from './features/ev-charger/schedules/DeviceChargingSchedule';
import { DeviceWifiConfiguration } from './features/ev-charger/wifi-configuration/DeviceWifiConfiguration';
import { DeviceWifiConnection } from './features/ev-charger/wifi-configuration/DeviceWifiConnection';
import { Generator } from './features/generator/Generator';
import { Home } from './features/home/Home';
import { LiveEnergyFlow } from './features/live-energy-flow/LiveEnergyFlow';
import { NotificationsArchive } from './features/notifications/NotificationsArchive';
import { NotificationsList } from './features/notifications/NotificationsList';
import { NotificationsListByType } from './features/notifications/NotificationsListByType';
import { ViewNotification } from './features/notifications/ViewNotification';
import { EditInvestmentsForm } from './features/savings/EditInvestmentsForm';
import { SavingsHome } from './features/savings/SavingsHome';
import { ConsentError } from './features/site/ConsentError';
import { LocationInfo } from './features/site/new-user-journey/location-permission/LocationInfo';
import { LocationRequiredError } from './features/site/new-user-journey/location-permission/LocationRequiredError';
import { RequireLocationPermission } from './features/site/new-user-journey/location-permission/RequireLocationPermission';
import { SetInstallerConsent } from './features/site/new-user-journey/SetInstallerConsent';
import { SetupHardware } from './features/site/new-user-journey/SetupHardware';
import { Site } from './features/site/Site';
import { SiteRedirect } from './features/site/SiteRedirect';
import { CreateSiteForm } from './features/sites/CreateSiteForm';
import { ListSites } from './features/sites/ListSites';
import { RedirectToSites } from './features/sites/RedirectToSites';
import { UpdateSiteForm } from './features/sites/UpdateSiteForm';
import { Solar } from './features/solar/Solar';
import { ConfirmFlatTariff } from './features/tariff/flat-tariff/ConfirmFlatTariff';
import { FlatTariffRates } from './features/tariff/flat-tariff/FlatTariffRates';
import { EditTariffSeasons } from './features/tariff/tariff-season/EditTariffSeasons';
import { TariffDetails } from './features/tariff/TariffDetails';
import { TariffList } from './features/tariff/TariffList';
import { TariffRoutes } from './features/tariff/TariffRoutes';
import { TariffSetup } from './features/tariff/TariffSetup';
import { TariffSetupComplete } from './features/tariff/TariffSetupComplete';
import { TariffSetupRoutes } from './features/tariff/TariffSetupRoutes';
import { ConfirmTieredTariff } from './features/tariff/tiered-tariff/ConfirmTieredTariff';
import { TieredTariffRates } from './features/tariff/tiered-tariff/TieredTariffRates';
import { ConfirmTOUTariff } from './features/tariff/tou-tariff/ConfirmTOUTariff';
import { TOUTariffRates } from './features/tariff/tou-tariff/TOUTariffRates';

// import { NotificationSettingsHome } from './features/account/settings/NotificationSettingsHome';
// import { SEHomeNotificationSettings } from './features/account/settings/SEHomeNotificationSettings';

export interface Route {
  path: string;
  element: ReactElement;
  children?: Route[];
  isPublic?: boolean;
}

export const routes: Route[] = [
  {
    element: <SiteRedirect />,
    path: '/',
    isPublic: true,
  },
  {
    element: <Login />,
    path: '/login',
    isPublic: true,
  },
  {
    element: <Logout />,
    path: '/logout',
    isPublic: true,
  },
  {
    element: <UpdateRequired />,
    path: '/update-required',
    isPublic: true,
  },
  {
    element: <RedirectToSites />,
    path: '/site/:id/*',
  },
  {
    path: '/sites',
    element: <Outlet />,
    children: [
      {
        path: '',
        element: <Navigate to="list" />,
      },
      {
        path: 'list',
        element: <ListSites />,
      },
      {
        path: 'create',
        element: <CreateSiteForm />,
      },
      {
        element: <Site />,
        path: ':id',
        children: [
          {
            path: '',
            element: <Navigate replace to="home" />,
          },
          {
            path: 'home',
            element: <Home />,
          },
          {
            path: 'activity',
            element: <Activity />,
          },
          {
            path: 'devices',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="list" />,
              },
              {
                path: 'list',
                element: <DevicesHome />,
              },
              {
                path: ':deviceId',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="view" />,
                  },
                  {
                    path: 'view',
                    element: <ViewDevice />,
                  },
                  {
                    path: 'edit',
                    element: <EditDevice />,
                  },
                ],
              },
              {
                path: 'add',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <AddDeviceHome />,
                  },
                ],
              },
            ],
          },
          {
            path: 'switches',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="../devices/list" />, // This meant to be a redirect to devices,
              },
              {
                path: ':switchId',
                element: <Outlet />,
                children: [
                  {
                    path: 'view',
                    element: <ViewSwitch />,
                  },
                  {
                    path: 'edit',
                    element: <EditSwitch />,
                  },
                ],
              },
            ],
          },
          {
            path: 'ayla-switches',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="../devices/list" />, // This meant to be a redirect to devices
              },
              {
                path: ':switchId',
                element: <Outlet />,
                children: [
                  {
                    path: 'view',
                    element: <ViewAylaDevice />,
                  },
                  {
                    path: 'details',
                    element: <AylaDeviceDetails />,
                  },
                  {
                    path: 'edit',
                    element: <EditAylaDevice />,
                  },
                ],
              },
            ],
          },
          {
            path: 'wifi-devices',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="../devices/list" />, // This meant to be a redirect to devices
              },
              {
                path: 'add',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    /* istanbul ignore next -- @preserve */
                    element: <Navigate replace to="start" />,
                  },
                  {
                    path: 'start',
                    element: <WifiDeviceStart />,
                  },
                  {
                    path: 'local-setup',
                    element: <WifiDeviceLocalSetup />,
                  },
                  {
                    path: 'register',
                    element: <AylaDeviceRegistration />,
                  },
                ],
              },
            ],
          },
          {
            path: 'matter-devices',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="../devices/list" />, // This meant to be a redirect to devices
              },
              {
                path: 'add',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    /* istanbul ignore next -- @preserve */
                    element: <Navigate replace to={IS_IOS ? 'start-ios' : 'start'} />,
                  },
                  {
                    path: 'start',
                    element: <AndroidCommissioningWizard />,
                  },
                  {
                    path: 'start-ios',
                    element: <IOSCommissioningWizard />,
                  },
                ],
              },
              {
                path: ':switchId',
                element: <Outlet />,
                children: [
                  {
                    path: 'view',
                    element: <ViewAylaDevice />,
                  },
                  {
                    path: 'details',
                    element: <AylaDeviceDetails />,
                  },
                  {
                    path: 'edit',
                    element: <EditAylaDevice />,
                  },
                ],
              },
            ],
          },
          {
            path: 'notifications',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="list" />,
              },
              {
                path: 'list',
                element: <NotificationsList />,
              },
              {
                path: 'list_by_type/:type',
                element: <NotificationsListByType />,
              },
              {
                path: ':notificationId/view',
                element: <ViewNotification />,
              },
              {
                path: 'archive',
                element: <NotificationsArchive />,
              },
            ],
          },
          {
            path: 'account',
            element: <Account />,
            children: [
              {
                path: '',
                element: <Navigate replace to="dashboard" />,
              },
              {
                path: 'dashboard',
                element: <AccountDashboard />,
              },
              {
                path: 'personal-details',
                element: <PersonalDetails />,
              },
              {
                path: 'vpp',
                element: <VPPRoutes />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <VPPHome />,
                  },
                  {
                    path: 'enrol',
                    element: <EnrolmentForm />,
                  },
                  {
                    path: 'join-program',
                    element: <JoinProgram />,
                  },
                  {
                    path: 'active-program',
                    element: <ActiveProgram />,
                  },
                ],
              },
              {
                path: 'energy-modes',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <EnergyModesHome />,
                  },
                  {
                    path: 'start',
                    element: <EnergyModesOnboardingStart />,
                  },
                  {
                    path: 'finish',
                    element: <EnergyModesOnboardingFinish />,
                  },
                  {
                    path: 'tou-savings-mode',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <DesignatedCircuits />,
                      },
                      {
                        path: 'add',
                        element: <AddDesignatedCircuit />,
                      },
                    ],
                  },
                  {
                    path: 'low-power-mode',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <DesignatedCircuits />,
                      },
                      {
                        path: 'add',
                        element: <AddDesignatedCircuit />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'hardware',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="list" />,
                  },
                  {
                    path: 'list',
                    element: <HardwareList />,
                  },
                  {
                    path: ':deviceId',
                    element: <Outlet />,
                    children: [
                      {
                        path: 'view',
                        element: <HardwareDetails />,
                      },
                      {
                        path: 'wifi',
                        element: <HardwareConnection connectionType="WIFI" />,
                      },
                      {
                        path: 'ethernet',
                        element: <HardwareConnection connectionType="ETHERNET" />,
                      },
                    ],
                  },
                  {
                    path: 'configure',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'home',
                        element: <HardwareConfiguration />,
                      },
                      {
                        path: 'inverter',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <InverterStart />,
                          },
                          {
                            path: 'power_up_instructions',
                            element: <InverterPowerUpInstructions />,
                          },
                          {
                            path: 'scan',
                            element: <InverterScanBarcode />,
                          },
                          {
                            path: 'enter_wifi_credentials',
                            element: <InverterWifiCredentials />,
                          },
                          {
                            path: 'finish',
                            element: <InverterSetupComplete />,
                          },
                          {
                            path: 'pending_inverter_internet_connection',
                            element: <InverterPendingInternetConnection />,
                          },
                        ],
                      },
                      {
                        path: 'meter',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <MeterStart />,
                          },
                          {
                            path: 'power_up_instructions',
                            element: <MeterPowerUpInstructions />,
                          },
                          {
                            path: 'connect_instructions',
                            element: <MeterConnectInstructions />,
                          },
                          {
                            path: 'scan',
                            element: <MeterScanBarcode />,
                          },
                          {
                            path: 'manual_serial_entry',
                            element: <MeterManualSerialEntry />,
                          },
                          {
                            path: 'connection_tests',
                            element: <MeterConnectionTests />,
                          },
                          {
                            path: 'register',
                            element: <MeterRegistration />,
                          },
                          {
                            path: 'finish',
                            element: <MeterSetupComplete />,
                          },
                        ],
                      },

                      {
                        path: 'tesla',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <TeslaConnect />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'settings',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <SettingsHome />,
                  },
                  /*
                   {
                    path: 'notifications',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to='home' />,
                      },
                      {
                        path: 'home',
                        element: <NotificationSettingsHome />,
                      },
                      {
                        path: 'schneider_home',
                        element: <SEHomeNotificationSettings />,
                      },
                    ]
                  },
                   */
                  {
                    path: 'family-members',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="view" />,
                      },
                      {
                        path: 'view',
                        element: <ViewFamilyMembers />,
                      },
                      {
                        path: 'edit',
                        element: <EditFamilyMembers />,
                      },
                    ],
                  },
                  {
                    path: 'widgets',
                    element: <WidgetSettings />,
                  },
                  {
                    path: 'wifi',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="view" />,
                      },
                      {
                        path: 'view',
                        element: <ViewWifiSettings />,
                      },
                      {
                        path: 'edit',
                        element: <EditWifiDetails />,
                      },
                      {
                        path: 'add',
                        element: <AddWifiDetails />,
                      },
                    ],
                  },
                  {
                    path: 'privacy',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <PrivacySettingsHome />,
                      },
                      {
                        path: 'delete-account',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <DeleteAccountStart />,
                          },
                          {
                            path: 'request-data-archive',
                            element: <DeleteAccountArchive />,
                          },
                          {
                            path: 'verify-details',
                            element: <DeleteAccountVerify />,
                          },
                          {
                            path: 'confirm-deletion',
                            element: <DeleteAccountConfirm />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'licenses',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <LicensesPublishersList />,
                      },
                      {
                        path: ':publisherId',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <LicensesPackagesList />,
                          },
                          {
                            path: ':packageId',
                            element: <LicensesDetail />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'language',
                    element: <SettingsLanguage />,
                  },
                ],
              },
              {
                path: 'tariff',
                element: <TariffRoutes />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <TariffList />,
                  },
                  {
                    path: 'finish',
                    element: <TariffSetupComplete />,
                  },
                  {
                    path: ':tariffId',
                    element: <TariffSetupRoutes />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'season-select',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'home',
                        element: <TariffSetup />,
                      },
                      {
                        path: 'tariff-details',
                        element: <TariffDetails />,
                      },
                      {
                        path: 'select-season',
                        element: <EditTariffSeasons />,
                      },
                      {
                        path: 'flat',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <FlatTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmFlatTariff />,
                          },
                        ],
                      },
                      {
                        path: 'tou',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <TOUTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmTOUTariff />,
                          },
                        ],
                      },
                      {
                        path: 'tiered',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <TieredTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmTieredTariff />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'support',
                element: <Support />,
              },
            ],
          },
          {
            path: 'update',
            element: <UpdateSiteForm />,
          },
          {
            path: 'set_installer_consent',
            element: <SetInstallerConsent />,
          },
          {
            path: 'live_energy_flow',
            element: <LiveEnergyFlow />,
          },
          {
            path: 'location_info',
            element: <LocationInfo />,
          },
          {
            path: 'location_required_error',
            element: <LocationRequiredError />,
          },
          {
            path: 'first_time_hardware_configuration',
            element: (
              <RequireLocationPermission>
                <SetupHardware />
              </RequireLocationPermission>
            ),
          },
          {
            path: 'ev-charger',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <EVChargerHome />,
                children: [
                  {
                    path: 'device-register',
                    element: <DeviceRegister />,
                  },
                  {
                    path: 'device-power-up-instructions',
                    element: <DevicePowerUpInstructions />,
                  },
                  {
                    path: 'device-wifi-configuration',
                    element: <DeviceWifiConfiguration />,
                  },
                  {
                    path: 'device-wifi-connection',
                    element: <DeviceWifiConnection />,
                  },
                  {
                    path: 'device-scan',
                    element: <DeviceScan />,
                  },
                  {
                    path: 'device-join',
                    element: <DeviceJoin />,
                  },
                  {
                    path: 'setup-success',
                    element: <SetupSuccess />,
                  },
                  {
                    path: 'wifi-setup-fail',
                    element: <WifiSetupFail />,
                  },
                  {
                    path: 'setup-fail',
                    element: <SetupFail />,
                  },
                  {
                    path: 'device-details',
                    element: <DeviceDetails />,
                  },
                  {
                    path: 'device-status',
                    element: <DeviceStatus />,
                  },
                  {
                    path: 'device-not-found',
                    element: <NoDevice />,
                  },
                  {
                    path: 'device-removed',
                    element: <NoDevice isDeviceRemoved={true} />,
                  },
                  {
                    path: 'device-charging-activity',
                    element: <ChargingActivity />,
                  },
                  {
                    path: 'device-charging-schedule',
                    element: <DeviceChargingSchedule />,
                  },
                ],
              },
            ],
          },
          {
            path: 'solar',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <Solar />,
              },
            ],
          },
          {
            path: 'generator',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <Generator />,
              },
            ],
          },
          {
            path: 'eco',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <Eco />,
              },
            ],
          },
          {
            path: 'savings',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <SavingsHome />,
              },
              {
                path: 'edit_investment_cost',
                element: <EditInvestmentsForm />,
              },
            ],
          },
          {
            path: 'battery',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="home" />,
              },
              {
                path: 'home',
                element: <BatteryHome />,
              },
              {
                path: 'set-reserve-capacity',
                element: <SetReserveCapacity />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <ConsentError />,
    path: '/consent-error',
  },
  {
    element: <TeslaOAuthRedirect />,
    path: '/app/tesla',
    isPublic: true,
  },
  {
    element: <RouteNotFound />,
    path: '*',
  },
];
